import React, { memo } from 'react';
import {
  Box,
  ButtonIcon,
  Chip,
  Icon,
  Menu,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { CF_SAV_BLD, CF_SAV_MED, IS_ELIGIBLE, testIds } from '../../constants';

import './ModuleHeader.css';

interface ModuleHeaderProps {
  title: string;
  progress: number;
  isCollapsed: boolean;
  onReset?: () => void;
  onClose?: () => void;
  dataTestId?: string;
}

/**
 * ModuleHeader
 */
function ModuleHeader({
  title,
  progress,
  isCollapsed,
  onReset,
  onClose,
  dataTestId,
}: ModuleHeaderProps) {
  return (
    <Box
      tag='header'
      className={cx('ModuleHeader', isCollapsed && 'ModuleHeader--isCollapsed')}
      dataTestId={dataTestId}>
      <Box tag='div' className='ModuleHeader-title'>
        <Icon kind='SaviCompanionGradient' color={CF_SAV_MED} />
        <Typography kind='h3' color={CF_SAV_BLD}>
          {title}
        </Typography>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
        {IS_ELIGIBLE && (
          <Chip
            size='xSmall'
            color='warning'
            label={`${progress} %`}
            leftIcon={<Icon kind='StatusToDo' />}
          />
        )}
      </Box>
      <Box tag='div' className='ModuleHeader-controls'>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
        {!isCollapsed && IS_ELIGIBLE && (
          <Chip
            size='xSmall'
            color='pink'
            kind='subtleFilled'
            label='180h Saved'
            leftIcon={<Icon kind='SaviCompanionGradient' />}
          />
        )}
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
        {!isCollapsed && IS_ELIGIBLE && (
          <Menu
            options={[
              {
                label: 'Cancel Session',
                value: 'cancel_session',
              },
              {
                label: 'New Request',
                value: 'new_request',
              },
            ]}
            onChange={() => {}}
            trigger={
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel='More'
                icon={
                  <Icon kind='MoreVertical' color='neutral-100' size='medium' />
                }
                dataTestId={testIds.GEN_MODULE_HEADER_MENU_BTN}
              />
            }
          />
        )}
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
        {IS_ELIGIBLE && (
          <ButtonIcon
            kind='saviGhost'
            ariaLabel='Collapse'
            icon={
              <Icon
                kind='Fix'
                size='medium'
                rotate={isCollapsed ? '0' : '180'}
              />
            }
            onClick={onReset}
            dataTestId={testIds.GEN_MODULE_HEADER_RESET_BTN}
          />
        )}
        {!isCollapsed && (
          <ButtonIcon
            kind='saviGhost'
            ariaLabel='Close Savi'
            icon={<Icon kind='Close' size='medium' />}
            onClick={() => {
              if (onClose) {
                onClose();
              }
            }}
            dataTestId={testIds.GEN_MODULE_HEADER_CLOSE_BTN}
          />
        )}
      </Box>
    </Box>
  );
}

ModuleHeader.defaultProps = {
  onReset: undefined,
  onClose: undefined,
  dataTestId: null,
};

export default memo(ModuleHeader);
